/**
 * FirePerimeterLayerSync — bridges the firePerimeterOverlay event stream to
 * the LayerDrawer 2.0 "live layers" registry, so each currently-drawn
 * perimeter appears in the Active Layers panel with its own visibility
 * toggle (matching the pattern Nomad uses for fire-model outputs).
 *
 * Headless component. Mount once inside <LayerDrawerProvider>.
 *
 * Flow:
 *   user clicks "Show Perimeter" (menu / tab)
 *     → toggle-fire-perimeter event
 *     → firePerimeterOverlay adds source/layer, emits fire-perimeter-shown
 *     → this component calls registerLiveLayer(...) the FIRST time,
 *       or updateLiveLayerState(id, true) on subsequent shows
 *     → drawer row appears (or reappears)
 *
 *   user clicks the drawer's toggle for that row
 *     → toggle callback dispatches show- / hide-fire-perimeter
 *     → firePerimeterOverlay adds/removes source/layer, emits *-shown / *-hidden
 *     → this component updates the live-layer enabled state
 *
 * Why we don't re-register on subsequent shows:
 *   LayerDrawerProvider.registerLiveLayer has a "persisted state restore"
 *   path that, if it sees a persisted enabled=false from a prior session
 *   while we're trying to register with enabled=true, calls our toggle
 *   callback with `false` to "restore" the previous state — which
 *   immediately hides the polygon we just drew. Caching the registration
 *   state and using updateLiveLayerState for re-shows avoids that race.
 *
 * Why we cache the show detail (geometry/label/description):
 *   The drawer toggle callback is fired with no event payload, only the
 *   layer id and the new enabled bool. If the user originally showed a
 *   historic perimeter (passing geometry directly from the Perimeters
 *   tab), we need to use that exact geometry on subsequent re-shows
 *   instead of falling back to fetching the *current* perimeter for
 *   the fire.
 */
import { useEffect, useRef } from 'react';
import { useLayerDrawer } from '../layerDrawer';

interface ShownDetail {
  fireNumber: string;
  perimeterId?: string;
  geometry?: GeoJSON.Geometry;
  label?: string;
  description?: string;
}

function liveLayerId(d: ShownDetail): string {
  return `fire-perimeter-${d.perimeterId ?? `current-${d.fireNumber}`}`;
}

function liveLayerName(d: ShownDetail): string {
  if (d.label) return d.label;
  return d.perimeterId
    ? `${d.fireNumber} perimeter (archived)`
    : `${d.fireNumber} perimeter`;
}

export const FirePerimeterLayerSync: React.FC = () => {
  const { registerLiveLayer, updateLiveLayerState, liveLayers } = useLayerDrawer();

  // Keep refs that the event listeners can read without re-binding when
  // liveLayers changes. The useEffect below only runs once on mount.
  const liveLayersRef = useRef(liveLayers);
  useEffect(() => { liveLayersRef.current = liveLayers; }, [liveLayers]);

  // Per-layer cache of the original show detail, so drawer re-toggles
  // restore the exact same perimeter (historic vs current, with its
  // geometry / labels) rather than triggering a fresh fetch.
  const detailCacheRef = useRef<Map<string, ShownDetail>>(new Map());

  useEffect(() => {
    const onShown = (ev: Event) => {
      const detail = (ev as CustomEvent<ShownDetail>).detail;
      if (!detail?.fireNumber) return;
      const id = liveLayerId(detail);
      detailCacheRef.current.set(id, detail);

      const alreadyRegistered = liveLayersRef.current.some(l => l.id === id);
      if (alreadyRegistered) {
        // Existing registry entry — just flip enabled. Avoids the
        // persisted-state restore race in registerLiveLayer.
        updateLiveLayerState(id, true);
        return;
      }

      registerLiveLayer(
        {
          id,
          name: liveLayerName(detail),
          shortName: detail.fireNumber,
          icon: 'fa-solid fa-fire',
          description: detail.description || 'Uploaded fire perimeter',
          enabled: true,
        },
        (enabled) => {
          const cached = detailCacheRef.current.get(id);
          if (enabled) {
            window.dispatchEvent(
              new CustomEvent('show-fire-perimeter', {
                detail: {
                  fireNumber: detail.fireNumber,
                  perimeterId: detail.perimeterId,
                  geometry: cached?.geometry,
                  label: cached?.label,
                  description: cached?.description,
                  flyTo: false,
                },
              }),
            );
          } else {
            window.dispatchEvent(
              new CustomEvent('hide-fire-perimeter', {
                detail: { fireNumber: detail.fireNumber, perimeterId: detail.perimeterId },
              }),
            );
          }
        },
      );
    };

    const onHidden = (ev: Event) => {
      const detail = (ev as CustomEvent<ShownDetail>).detail;
      if (!detail?.fireNumber) return;
      // Keep the live-layer record in the registry with enabled=false so
      // the Active tab's "lingering disabled" UX can hold a fading row for
      // a few seconds, and so subsequent shows can re-toggle without
      // re-registering. Detail cache is preserved for re-toggle fidelity.
      updateLiveLayerState(liveLayerId(detail), false);
    };

    window.addEventListener('fire-perimeter-shown', onShown);
    window.addEventListener('fire-perimeter-hidden', onHidden);
    return () => {
      window.removeEventListener('fire-perimeter-shown', onShown);
      window.removeEventListener('fire-perimeter-hidden', onHidden);
    };
  }, [registerLiveLayer, updateLiveLayerState]);

  return null;
};
